// Phase 7 — Track B T-B4. Dates form validation.
//
// Mirrors the backend's `createEventBodySchema` / `updateEventBodySchema` in
// `src/modules/dates/routes/events.ts`. Reminders are an array of
// `{ offset_days }` (non-negative integers, capped at 3650 ≈ 10 years).
//
// Backend rules carried forward:
//   - `event_id` is caller-supplied (idempotency key); 1..128 chars.
//   - `label` is 1..200 chars.
//   - `occurs_on` is ISO `YYYY-MM-DD`.
//   - `recurrence` is one of `none` / `yearly` / `monthly`.
//   - `recurrence_interval` is a positive integer ≤ 1000.
//   - `notes` ≤ 2000 chars (nullable on update).
//   - `reminders` is an array of ≤ 20 items.
//   - update body must contain at least one field.
//
// Both schemas use `.strict()`.

import { z } from 'zod';

const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'must be ISO date YYYY-MM-DD');

const recurrenceSchema = z.enum(['none', 'yearly', 'monthly']);

const eventIdSchema = z.string().min(1).max(128);

const reminderInputSchema = z
  .object({
    offset_days: z.number().int().nonnegative().max(3650),
  })
  .strict();

export type ReminderInput = z.infer<typeof reminderInputSchema>;

export const createDateEventBodySchema = z
  .object({
    event_id: eventIdSchema,
    label: z.string().min(1).max(200),
    occurs_on: isoDateSchema,
    recurrence: recurrenceSchema,
    recurrence_interval: z.number().int().positive().max(1000).optional(),
    notes: z.string().max(2000).nullable().optional(),
    reminders: z.array(reminderInputSchema).max(20).optional(),
  })
  .strict();

export type CreateDateEventBody = z.infer<typeof createDateEventBodySchema>;

export const updateDateEventBodySchema = z
  .object({
    label: z.string().min(1).max(200).optional(),
    occurs_on: isoDateSchema.optional(),
    recurrence: recurrenceSchema.optional(),
    recurrence_interval: z.number().int().positive().max(1000).optional(),
    notes: z.string().max(2000).nullable().optional(),
    reminders: z.array(reminderInputSchema).max(20).optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, {
    message: 'patch must contain at least one field',
  });

export type UpdateDateEventBody = z.infer<typeof updateDateEventBodySchema>;

export { reminderInputSchema };
